<template>
    <div class="container max-w-3xl mx-auto mt-8 pt-8 md:pb-8" v-if="appointmentData">
        <div v-if="loading" class="mx-auto pt-20 pb-16 pl-4 text-center">
            <div class="loader"></div>
        </div>

        <div v-if="processingPayment" class="mx-auto pt-20 pb-16 pl-4 text-center">
            <div class="loader"></div>

            <div>Your payment is being processed. Please do not close or refresh the page.</div>
        </div>

        <div v-if="threeDomainSecure" class="w-full h-full">
            <iframe :src="getSecurePaymentUrl()"></iframe>
        </div>

        <div v-show="shouldShowForm()" class="w-full md:w-1/2 px-4 lg:px-0">
            <form>
                <h2 class="heading">Secure payment</h2>

                <div v-if="error" class="text-red mb-4">{{ error }}</div>

                <div class="form-field-group">
                    <div class="form-group">
                        <label for="patient-address1-input" class="sr-only">
                            Address Line 1 *
                        </label>

                        <input id="patient-address1-input"
                            name="address1"
                            v-model="address1"
                            class="form-control"
                            placeholder="Address *"
                            required
                            maxlength="60"
                            @blur="clearError"
                            @change="handleFieldChange"
                        />

                        <div v-if="errors.address1" class="form-error">{{ errors.address1 }}</div>
                    </div>

                    <div class="form-group">
                        <label for="patient-address2-input" class="sr-only">
                            Address Line 2
                        </label>

                        <input id="patient-address2-input"
                            name="address2"
                            v-model="address2"
                            class="form-control"
                            maxlength="60"
                            @change="handleFieldChange"
                        />
                    </div>

                    <div class="form-group">
                        <label for="patient-city-input" class="sr-only">
                            City
                        </label>

                        <input id="patient-city-input"
                            name="city"
                            v-model="city"
                            class="form-control"
                            placeholder="City *"
                            required
                            maxlength="60"
                            @change="handleFieldChange"
                        />
                    </div>

                    <div class="form-group">
                        <label for="patient-postcode-input" class="sr-only">
                            Postcode *
                        </label>

                        <input id="patient-postcode-input"
                            name="postcode"
                            v-model="postcode"
                            class="form-control"
                            placeholder="Postcode *"
                            required
                            maxlength="10"
                            @blur="clearError"
                            @change="handleFieldChange"
                        />

                        <div v-if="errors.postcode" class="form-error">{{ errors.postcode }}</div>
                    </div>
                </div>

                <div class="form-group">
                    <input type="checkbox"
                           class="checkbox"
                           id="payment-alt-billing-address"
                           name="alt-billing-address"
                           value="1"
                           v-model="altBillingAddress"
                    />

                    <label for="payment-alt-billing-address">
                        Use a different billing address
                    </label>
                </div>

                <div v-if="altBillingAddress">
                    <div class="form-group">
                        <label for="payment-address1-input" class="sr-only">
                            Address Line 1 *
                        </label>

                        <input id="payment-address1-input"
                               name="address1"
                               v-model="billingAddress1"
                               class="form-control"
                               placeholder="Address *"
                               required
                               maxlength="60"
                               @blur="clearError"
                        />

                        <div v-if="errors.billingAddress1" class="form-error">{{ errors.billingAddress1 }}</div>
                    </div>

                    <div class="form-group">
                        <label for="payment-address2-input" class="sr-only">
                            Address Line 2
                        </label>

                        <input id="payment-address2-input"
                               name="address2"
                               v-model="billingAddress2"
                               class="form-control"
                               maxlength="60"
                        />
                    </div>

                    <div class="form-group">
                        <label for="payment-city-input" class="sr-only">
                            City
                        </label>

                        <input id="payment-city-input"
                               name="city"
                               v-model="billingCity"
                               class="form-control"
                               placeholder="City *"
                               required
                               maxlength="60"
                        />

                        <div v-if="errors.billingCity" class="form-error">{{ errors.billingCity }}</div>
                    </div>


                    <div class="form-group">
                        <label for="payment-postcode-input" class="sr-only">
                            Postcode *
                        </label>

                        <input id="payment-postcode-input"
                               name="postcode"
                               v-model="billingPostcode"
                               class="form-control"
                               placeholder="Postcode *"
                               required
                               maxlength="10"
                               @blur="clearError"
                        />

                        <div v-if="errors.billingPostcode" class="form-error">{{ errors.billingPostcode }}</div>
                    </div>
                </div>

                <div id="sp-container"></div>

                <div v-if="preAuth" class="mb-4 text-sm">Your card will be charged £0.01 to confirm the booking and authorise the payment to be taken following the appointment.</div>

                <appointment-total class="mb-4 md:hidden" />

                <div id="submit-container" class="form-group">
                    <input type="submit" class="btn btn-primary" value="Book Now" :disabled="!isValid() || processingPayment" />
                </div>

                <div v-if="preAuth">
                    <p class="mb-2">We will charge your payment card the amount of {{ amountToPayString }} following the completion of your initial appointment and all subsequent appointments.</p>
                    <p class="mb-2">Appointments not attended will be chargeable at the amount of {{ amountToPayString }} unless notice has been given within 24 hours of the appointment.</p>
                    <p class="mb-2">Appointments that have not been attended and charged, may not be eligible for a claim back against your policy.</p>
                </div>
                <change-appointment-button class="md:hidden" />
            </form>
        </div>

        <div v-show="shouldShowForm()" class="hidden md:block w-1/2 mb-8 pl-20 pr-4 lg:pr-0 text-center">
            <appointment-info />

            <reserved-countdown />

            <appointment-total />
        </div>

        <add-voucher />

        <reserve-same-appointment />
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';

    import AddVoucher from './Modals/AddVoucher';
    import AppointmentInfo from './Cards/AppointmentInfo';
    import AppointmentTotal from './Cards/AppointmentTotal';
    import axios from '../../lib/Axios';
    import ChangeAppointmentButton from "./Cards/ChangeAppointmentButton";
    import ReservedCountdown from './Cards/ReservedCountdown';
    import ReserveSameAppointment from '../Appointments/ReserveSameAppointment';
    import SagePay from '../../lib/SagePay';
    import {Prices} from "@/lib/Prices";

    export default {
        name: 'payment-details',

        components: {
            AddVoucher,
            AppointmentInfo,
            AppointmentTotal,
            ChangeAppointmentButton,
            ReservedCountdown,
            ReserveSameAppointment,
        },

        computed: {
            ...mapState('appointment', [
                'appointmentData',
            ]),

            ...mapState('checkout', [
                'patientDetails',
                'injuryDetails',
                'paymentDetails',
            ]),

            ...mapState('voucher', [
                'voucherCode',
            ]),

            total() {
                if (this.appointmentData.clinic.ClinicID == process.env.VUE_APP_VIDEO_CLINIC_ID) {
                    return Prices.video();
                }

                if (this.appointmentData.clinic.ClinicTier === 'CentralLondon') {
                    return Prices.centralLondon();
                }

                if (Prices.hasExceptionPrice() && window._.includes(
                    Prices.exceptionClinicIds(),
                    parseInt(this.appointmentData.clinic.ClinicID))
                ) {
                    return Prices.exception();
                }

                return this.appointmentData.clinic.InsideM25 ? Prices.insideM25() : Prices.outsideM25();
            },

            discount() {
                if (!this.voucherCode) {
                    return 0;
                }

                if (this.appointmentData.clinic.ClinicTier === 'CentralLondon') {
                    return this.voucherCode.discount_central_london;
                }

                if (this.appointmentData.clinic.ClinicTier === 'Virtual') {
                    return this.voucherCode.discount_video;
                }

                return this.appointmentData.clinic.InsideM25 ? this.voucherCode.discount_inside_m25 : this.voucherCode.discount_outside_m25;
            },

            amountToPay() {
                return this.total - this.discount;
            },

            amountToPayString() {
                return this.amountToPay.toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                });
            },

          preAuth() {
              return process.env.VUE_APP_PAYMENT_PREAUTH === 'true'
          }
        },

        data() {
            return {
                loading: true,
                processingPayment: false,
                error: null,
                merchantSessionKey: null,

                address1: '',
                address2: '',
                city: '',
                postcode: '',

                altBillingAddress: false,
                billingAddress1: '',
                billingAddress2: '',
                billingCity: '',
                billingPostcode: '',

                errors: {},

                sagePay: null,
                cardIdentifier: null,
                threeDomainSecure: false,

                redirectForm: {
                    AcsUrl: null,
                    PaReq: null,
                    TermUrl: null,
                    MD: null,
                },
            };
        },

        mounted() {
            this.$gtm.trackView('Booking Payment', '/booking/payment');

            this.$scrollTo('#app');

            this.address1 = this.patientDetails.address1;
            this.address2 = this.patientDetails.address2;
            this.city = this.patientDetails.city;
            this.postcode = this.patientDetails.postcode;

            this.altBillingAddress = this.paymentDetails.altBillingAddress;
            this.billingAddress1 = this.paymentDetails.address1;
            this.billingAddress2 = this.paymentDetails.address2;
            this.billingCity = this.paymentDetails.city;
            this.billingPostcode = this.paymentDetails.postcode;

            this.$watch('address1', this.syncStoreValues);
            this.$watch('address2', this.syncStoreValues);
            this.$watch('city', this.syncStoreValues);
            this.$watch('postcode', this.syncStoreValues);

            this.$watch('altBillingAddress', this.syncStoreValues);
            this.$watch('billingAddress1', this.syncStoreValues);
            this.$watch('billingAddress2', this.syncStoreValues);
            this.$watch('billingCity', this.syncStoreValues);
            this.$watch('billingPostcode', this.syncStoreValues);

            this.listen('appointment.expired', this.reservedAppointmentExpired);
            this.listen('appointment.error', this.onAppointmentError);
            this.listen('payment.error', this.onPaymentFailed);
            this.listen('payment.success', this.onPaymentSuccess);

            this.initSagePay();
        },

        beforeDestroy() {
            this.destroySagePay();
        },

        destroyed() {
            this.silence('appointment.expired', this.reservedAppointmentExpired);
            this.silence('appointment.error', this.onAppointmentError);
            this.silence('payment.error', this.onPaymentFailed);
            this.silence('payment.success', this.onPaymentSuccess);
        },

        methods: {
            ...mapActions('checkout', [
                'setPatientDetails',
                'setPatientDetailsValid',
                'setPatientDetailsInvalid',
            ]),

            ...mapActions('checkout', [
                'setPaymentDetails',
                'setPaymentDetailsValid',
                'setPaymentDetailsInvalid',
            ]),

            handleFieldChange(e) {
                if (e.target.value) {
                    this.trackFieldChange(e.target.name, e.target.value);
                }
            },

            trackFieldChange(field) {
                this.$gtm.trackEvent({
                    event: 'fieldcomplete',
                    fieldname: field,
                });
            },

            shouldShowForm() {
                return !this.loading && !this.processingPayment && !this.threeDomainSecure;
            },

            onPaymentFailed() {
                this.destroySagePay();

                this.cardIdentifier = null;

                this.threeDomainSecure = false;

                this.redirectForm = {
                    AcsUrl: null,
                    PaReq: null,
                    TermUrl: null,
                    MD: null,
                };

                this.initSagePay();

                if (!this.error) {
                    this.error = 'Sorry, but we were unable to take payment. Please try again or get in touch with us.';
                }
            },

            onAppointmentError() {
                this.error = 'Sorry, there was an error booking your appointment. Please try again or get in touch with us.';
                this.onPaymentFailed();
            },

            reservedAppointmentExpired() {
                this.onPaymentFailed();
                this.error = null;
            },

            destroySagePay() {
                try {
                    this.sagePay.destroy();
                } catch (error) {
                    // Fall through.
                }
            },

            requiredFields() {
                let fields = ['address1', 'city', 'postcode'];

                if (this.altBillingAddress) {
                    fields.push('billingAddress1');
                    fields.push('billingCity');
                    fields.push('billingPostcode');
                }

                return fields;
            },

            syncStoreValues() {
                this.setPatientDetails({
                    title: this.patientDetails.title,
                    firstName: this.patientDetails.firstName,
                    lastName: this.patientDetails.lastName,
                    phone: this.patientDetails.phone,
                    email: this.patientDetails.email,
                    marketing: this.patientDetails.marketing,
                    address1: this.address1,
                    address2: this.address2,
                    city: this.city,
                    postcode: this.postcode,
                });

                this.setPaymentDetails({
                    altBillingAddress: this.altBillingAddress,
                    address1: window._.trim(this.billingAddress1) || null,
                    address2: window._.trim(this.billingAddress2) || null,
                    city: window._.trim(this.billingCity) || null,
                    postcode: window._.trim(this.billingPostcode) || null,
                });

                if (this.isValid()) {
                    this.setPaymentDetailsValid();
                    this.setPatientDetailsValid();
                    return true;
                } else {
                    this.setPaymentDetailsInvalid();
                    this.setPatientDetailsInvalid();
                    return false;
                }
            },

            isValid() {
                let hasError = false, value, hasValue;

                // Check that we have a value for all of the required fields.
                const requiredFields = this.requiredFields();

                for (let i in requiredFields) {
                    if (requiredFields.hasOwnProperty(i)) {
                        value = this[requiredFields[i]];
                        hasValue = typeof value === 'boolean' ? !!value : window._.trim(value);

                        if (!hasValue) {
                            if (this.submitting) {
                                this.$set(this.errors, requiredFields[i], 'Please fill in this field.');
                            }

                            hasError = true;
                        } else {
                            this.$delete(this.errors, requiredFields[i]);
                        }
                    }
                }

                return !hasError;
            },

            clearError(e) {
                this.$delete(this.errors, e.target.name);
            },

            initSagePay() {
                // Load up the SagePay JS library.
                SagePay.load(() => {
                    // Then get a merchant session key.
                    SagePay.getMerchantSessionKey().then((data) => {
                        this.merchantSessionKey = data;

                        // Now show the form.
                        this.sagePay = window.sagepayCheckout({
                            merchantSessionKey: this.merchantSessionKey.merchantSessionKey,
                            onTokenise: (result) => {
                                if (result.success) {
                                    this.cardIdentifier = result.cardIdentifier;
                                    this.processPayment();
                                } else {
                                    if (result.code === 1002) {
                                        SagePay.getMerchantSessionKey().then((data) => {
                                            this.merchantSessionKey = data;
                                            this.error = 'Your token has been refreshed. Please re-submit the form to complete your booking.';
                                        });
                                    } else {
                                        this.error = result.error.errorMessage || 'Sorry, there was a problem submitting your payment.';
                                    }

                                    this.processingPayment = false;
                                }
                            },
                        });

                        this.sagePay.form();

                        this.loading = false;
                    });
                });
            },

            onPaymentSuccess() {
                this.storeDetailsInLocalStorage();

                this.$router.push({ name: 'appointment-confirmation' });
            },

            storeDetailsInLocalStorage() {
                window.localStorage.setItem('appointment', JSON.stringify(this.appointmentData));
            },

            processPayment() {
                this.processingPayment = true;

                let memberId = null;
                let groupId = null;
                let expiry = null;

                try {
                    const raw = window.localStorage.getItem('memberParams');

                    if (raw) {
                        const mp = JSON.parse(raw);
                        memberId = mp.userId || null;
                        groupId = mp.groupId || null;
                        expiry = mp.expiry || null;
                    }
                } catch (e) {
                    // ignore parse/localStorage errors
                }

                let area;
                if (this.injuryDetails.isHealthCheck) {
                    area = 'health_check';
                } else if (this.injuryDetails.clientKey) {
                    area = 'DIGITALTRIAGEKEY';
                } else {
                    area = this.injuryDetails.bodyPart;
                }

                let colorDepth = screen.colorDepth;
                if (colorDepth === 30) {
                  // Chrome on Mac returns values that are not by spec.
                  // @see https://support.google.com/chrome/thread/53600062?hl=en
                  colorDepth = 24;
                }

                const payload = {
                    MerchantSessionKey: this.merchantSessionKey.merchantSessionKey,
                    CardIdentifier: this.cardIdentifier,
                    OrderData: {
                        AppointmentID: this.appointmentData.appointment.AppointmentID,
                        ClinicID: this.appointmentData.clinic.ClinicID,
                        DiscountCode: this.voucherCode ? window._.first(this.voucherCode.codes) : null,
                    },
                    BrowserData: {
                        JavascriptEnabled: true,
                        JavaEnabled: navigator.javaEnabled(),
                        Language: navigator.language,
                        ColorDepth: colorDepth,
                        ScreenHeight: screen.height,
                        ScreenWidth: screen.width,
                        TZ: new Date().getTimezoneOffset(),
                        Website: window.location.protocol + '//' + window.location.hostname
                    },
                    PatientData: {
                        FirstName: this.patientDetails.firstName,
                        LastName: this.patientDetails.lastName,
                        DOB: window.moment(this.injuryDetails.dateOfBirth).format('MM/DD/YYYY'),
                        Gender: this.injuryDetails.gender === 'other' ? this.injuryDetails.genderOther: this.injuryDetails.gender,
                        Phone: this.patientDetails.phone,
                        Email: this.patientDetails.email,
                        Address1: this.patientDetails.address1,
                        Address2: this.patientDetails.address2,
                        City: this.patientDetails.city,
                        Postcode: this.patientDetails.postcode,
                        BillingAddress1: this.altBillingAddress ? this.billingAddress1 : null,
                        BillingAddress2: this.altBillingAddress ? this.billingAddress2 : null,
                        BillingCity: this.altBillingAddress ? this.billingCity : null,
                        BillingPostcode: this.altBillingAddress ? this.billingPostcode : null,
                        AllowFutureContact: this.patientDetails.marketing,
                        SpecificAccessNeeds: this.injuryDetails.specificAccessNeeds,
                        SpecificAccessNeedsNotes: this.injuryDetails.specificAccessNeedsNotes,
                        AgreeToTermsAndConditions: this.injuryDetails.acceptTerms,
                        MemberId: memberId,
                        GroupId: groupId,
                        Expiry: expiry, 
                    },
                    InjuryData: {
                        IsNew: this.injuryDetails.newOrExisting === 'new',
                        Area: area,
                        AdditionalInfo: this.injuryDetails.additionalInfo,
                    },
                    DigitalTriageKey: this.injuryDetails.clientKey ? this.injuryDetails.clientKey : ''
                };

                axios.post('v1/payment/process', payload)
                    .then(this.handleProcessPaymentResponse)
                    .catch(this.handleProcessPaymentError);
            },

            handleProcessPaymentResponse(response) {
                if (!response.data.success) {
                    if (response.data.redirect) {
                        let threeDSSessionData = atob(response.data.id).split(':');
                        if (response.data.redirect.website !== 'undefined') {
                          threeDSSessionData.push(encodeURIComponent(response.data.redirect.website));
                        }

                        this.$set(this.redirectForm, 'AcsUrl', response.data.redirect.acsUrl);
                        this.$set(this.redirectForm, 'creq', response.data.redirect.cReq);
                        this.$set(this.redirectForm, 'threeDSSessionData', btoa(threeDSSessionData.join(':')));

                        this.threeDomainSecure = true;

                        this.$scrollTo('#app');
                    } else {
                        if (response.data.message) {
                            this.error = response.data.message;
                        } else {
                            this.error = 'Sorry, but something has gone wrong. No payment has been taken.';
                        }

                        this.onPaymentFailed();
                    }
                } else {
                    this.dispatch('payment.success');
                }

                this.processingPayment = false;
            },

            handleProcessPaymentError() {
                this.error = 'Sorry, but something has gone wrong. No payment has been taken.';

                this.processingPayment = false;

                this.onPaymentFailed();
            },

            getSecurePaymentUrl() {
                const queryString = require('query-string');

                return '/secure-payment?' + queryString.stringify({
                    AcsUrl: this.redirectForm.AcsUrl,
                    creq: this.redirectForm.creq,
                    threeDSSessionData: this.redirectForm.threeDSSessionData,
                });
            },
        },
    }
</script>

<style lang="scss" scoped>
    .container {
        @apply flex flex-col;

        @screen md {
            @apply flex-row justify-between;
        }
    }

    iframe {
        width: 100%;
        min-height: 800px;
        border: 0 none;
    }
</style>
